import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MemberPicker, type MemberCandidate } from "../MemberPicker.js";
import type { ActionItemCode, RASCISnapshot } from "../../lib/actionItems.js";
import type { EscalationPolicy } from "../../lib/escalation.js";

/**
 * ADR-0042 — Response entry forms.
 *
 * Each of the three shapes (action_item / email / webhook) has its own
 * sub-form. All three are embedded in the watch node inspector's Responses
 * section (no separate response node type exists).
 *
 * Each response carries a client-generated `id` (UUID) that becomes
 * `source_ref.response_id` on watch-created action items — used for refire
 * and auto-resolve correlation.
 */

type Severity = "critical" | "high" | "medium" | "low";
type Priority = "critical" | "high" | "medium" | "low";

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];
const PRIORITIES: Priority[] = ["critical", "high", "medium", "low"];

export type ResponseType = "action_item" | "email" | "webhook";

/** ADR-0075 — extends the RASCI snapshot with per-slot mode + role-type
 *  fields. The hub validates these alongside the existing fields, and the
 *  dispatcher reads them at fire time. Stored as part of the response config's
 *  rasci block.
 *
 *  Accountable supports two modes (single-user only). R/S/C/I support four:
 *    - explicit          — explicit user/group pickers
 *    - customer_primary  — the row's customer's primary mapped user
 *    - customer_role     — all the row's customer's users with one role
 *    - customer_team     — every user mapped to the row's customer (any role)
 */
export type SlotMode = "explicit" | "customer_primary" | "customer_role" | "customer_team";
export type AccountableMode = "explicit" | "customer_primary";

export interface CustomerRasciExtension {
  accountable_mode?: AccountableMode;
  responsible_mode?: SlotMode;
  responsible_role_type_id?: string | null;
  support_mode?: SlotMode;
  support_role_type_id?: string | null;
  consulted_mode?: SlotMode;
  consulted_role_type_id?: string | null;
  informed_mode?: SlotMode;
  informed_role_type_id?: string | null;
}

export type WatchRasciSnapshot = Partial<RASCISnapshot> & CustomerRasciExtension;

export interface ActionItemResponseConfig {
  id: string;
  type: "action_item";
  item_type_code_id?: string;
  severity?: Severity;
  priority?: Priority;
  escalation_policy_id?: string | null;
  auto_resolve?: boolean;
  /** When auto_resolve is true, controls whether RASCI members are notified
   *  on the auto-close. Default false: auto-resolve is the "no action needed"
   *  signal — opt in only when closure visibility matters. */
  notify_on_auto_resolve?: boolean;
  rasci?: WatchRasciSnapshot;
}

export interface EmailResponseConfig {
  id: string;
  type: "email";
  recipient_user_ids?: string[];
  recipient_contact_ids?: string[];
  recipient_group_ids?: string[];
  subject_template?: string;
  body_template?: string;
}

export interface WebhookResponseConfig {
  id: string;
  type: "webhook";
  url?: string;
  method?: "POST" | "PUT";
  headers?: Record<string, string>;
  body_template?: string;
}

export type ResponseConfig = ActionItemResponseConfig | EmailResponseConfig | WebhookResponseConfig;

const INTERPOLATION_HELP =
  "Available in templates: {{operation_name}}, {{evaluated_at}}, {{row_count}}, " +
  "{{scalar_value}}, {{result_hash}}, {{previous_result_hash}}.";

export function makeResponseConfig(type: ResponseType): ResponseConfig {
  const id = crypto.randomUUID();
  if (type === "action_item") {
    return { id, type, severity: "medium", priority: "medium" };
  }
  if (type === "email") {
    return {
      id,
      type,
      recipient_user_ids: [],
      recipient_contact_ids: [],
      recipient_group_ids: [],
      subject_template: "",
      body_template: ""
    };
  }
  return { id, type, url: "", method: "POST", body_template: "" };
}

// ── Action item form ────────────────────────────────────────────────────────

export function ActionItemResponseForm({
  config,
  candidates,
  itemTypes,
  policies,
  onChange,
  dynamicMappingEnabled,
  customerColumn,
  customerRoleTypes
}: {
  config: ActionItemResponseConfig;
  candidates: MemberCandidate[];
  itemTypes: ActionItemCode[];
  policies: EscalationPolicy[];
  onChange: (next: ActionItemResponseConfig) => void;
  /** ADR-0075 §3.6 — when on, R/A/S/C/I slots gain mode toggles. */
  dynamicMappingEnabled?: boolean;
  customerColumn?: string | null;
  customerRoleTypes?: Array<{ id: string; label: string; slug: string; is_disabled: boolean }>;
}) {
  const [stakeholdersOpen, setStakeholdersOpen] = useState(false);
  const rasci = config.rasci ?? {};
  const responsibleIds = useMemo(
    () => [...(rasci.responsible_user_ids ?? []), ...(rasci.responsible_group_ids ?? [])],
    [rasci.responsible_user_ids, rasci.responsible_group_ids]
  );
  const supportIds = useMemo(
    () => [...(rasci.support_user_ids ?? []), ...(rasci.support_group_ids ?? [])],
    [rasci.support_user_ids, rasci.support_group_ids]
  );
  const consultedIds = useMemo(
    () => [...(rasci.consulted_user_ids ?? []), ...(rasci.consulted_group_ids ?? [])],
    [rasci.consulted_user_ids, rasci.consulted_group_ids]
  );
  const informedIds = useMemo(
    () => [...(rasci.informed_user_ids ?? []), ...(rasci.informed_group_ids ?? [])],
    [rasci.informed_user_ids, rasci.informed_group_ids]
  );

  function splitByKind(ids: string[]): { userIds: string[]; groupIds: string[] } {
    const byId = new Map(candidates.map((c) => [c.id, c]));
    const userIds: string[] = [];
    const groupIds: string[] = [];
    for (const id of ids) {
      const c = byId.get(id);
      if (!c) continue;
      if (c.kind === "group") groupIds.push(id);
      else userIds.push(id);
    }
    return { userIds, groupIds };
  }

  function patch(next: Partial<ActionItemResponseConfig>) {
    onChange({ ...config, ...next });
  }
  function patchRasci(next: Partial<WatchRasciSnapshot>) {
    patch({ rasci: { ...(rasci as WatchRasciSnapshot), ...next } });
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div className="field">
        <label className="label">Item type *</label>
        <select
          className="select"
          value={config.item_type_code_id ?? ""}
          onChange={(e) => patch({ item_type_code_id: e.target.value })}
        >
          <option value="">Select…</option>
          {itemTypes.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        {itemTypes.length === 0 && (
          <div className="help">
            No item types defined.{" "}
            <Link to="/app/admin/action-item-codes?kind=item-types">Configure codes</Link>.
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <label className="field">
          <span className="label">Severity *</span>
          <select
            className="select"
            value={config.severity ?? "medium"}
            onChange={(e) => patch({ severity: e.target.value as Severity })}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Priority *</span>
          <select
            className="select"
            value={config.priority ?? "medium"}
            onChange={(e) => patch({ priority: e.target.value as Priority })}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="field">
        <label className="label">Escalation policy</label>
        <select
          className="select"
          value={config.escalation_policy_id ?? ""}
          onChange={(e) => patch({ escalation_policy_id: e.target.value || null })}
        >
          <option value="">None</option>
          {policies.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <div className="help" style={{ marginTop: 4 }}>
          Manage policies at{" "}
          <Link to="/app/admin/escalation-policies">admin / escalation policies</Link>.
        </div>
      </div>

      <div className="field" style={{ display: "grid", gap: 4 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={config.auto_resolve === true}
            onChange={(e) =>
              patch({
                auto_resolve: e.target.checked,
                // Drop the notify flag when turning auto-resolve off so it doesn't
                // resurface in a stale state next time the box is checked.
                notify_on_auto_resolve: e.target.checked ? config.notify_on_auto_resolve : undefined
              })
            }
          />
          <span>Auto-resolve when signal clears</span>
        </label>
        {config.auto_resolve === true && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 24 }}>
            <input
              type="checkbox"
              checked={config.notify_on_auto_resolve === true}
              onChange={(e) => patch({ notify_on_auto_resolve: e.target.checked })}
            />
            <span>Notify RASCI when auto-resolved</span>
          </label>
        )}
        {config.auto_resolve === true && (
          <div className="help" style={{ paddingLeft: 24 }}>
            Default: silent. Turn on if closure visibility matters (operator should know the issue
            cleared without having to check the action items list).
          </div>
        )}
      </div>

      <div className="card" style={{ background: "var(--bg-subtle)", display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>RASCI</div>

        <fieldset className="rasciSlot rasciA">
          <div className="rasciSlotHeader">
            <span className="rasciBadge" aria-hidden="true">A</span>
            <legend className="label" style={{ margin: 0, padding: 0 }}>Accountable *</legend>
            {dynamicMappingEnabled && (
              <SlotSourceSelect
                value={rasci.accountable_mode ?? "explicit"}
                options={[
                  { value: "explicit", label: "Explicit user" },
                  { value: "customer_primary", label: "Customer's primary user" }
                ]}
                onChange={(m) => patchRasci({ accountable_mode: m as AccountableMode })}
              />
            )}
          </div>
          {(rasci.accountable_mode ?? "explicit") === "explicit" ? (
            <MemberPicker
              mode="single"
              allowedKinds={["user"]}
              candidates={candidates}
              selected={rasci.accountable_user_id ? [rasci.accountable_user_id] : []}
              onChange={(next) => patchRasci({ accountable_user_id: next[0] ?? "" })}
              placeholder="Pick an accountable user…"
            />
          ) : (
            <div className="help muted" style={{ fontSize: 12 }}>
              The row's customer's primary mapped user becomes the assignee. Walks the customer
              hierarchy on miss; falls back to default coverage.
            </div>
          )}
        </fieldset>

        <SlotBlock
          label="Responsible *"
          letter="R"
          help="Does the work. One or more users or groups who actively resolve the item."
          dynamicMappingEnabled={!!dynamicMappingEnabled}
          mode={(rasci.responsible_mode as SlotMode | undefined) ?? "explicit"}
          roleTypeId={rasci.responsible_role_type_id ?? ""}
          candidates={candidates}
          selectedIds={responsibleIds}
          roleTypes={customerRoleTypes ?? []}
          onModeChange={(m) => patchRasci({ responsible_mode: m })}
          onRoleTypeChange={(id) => patchRasci({ responsible_role_type_id: id || null })}
          onMemberChange={(next) => {
            const { userIds, groupIds } = splitByKind(next);
            patchRasci({ responsible_user_ids: userIds, responsible_group_ids: groupIds });
          }}
        />

        <div>
          <button
            type="button"
            className="btn btnSm"
            onClick={() => setStakeholdersOpen((v) => !v)}
            aria-expanded={stakeholdersOpen}
          >
            {stakeholdersOpen ? "− Additional stakeholders" : "+ Additional stakeholders"}
          </button>
        </div>

        {stakeholdersOpen && (
          <div style={{ display: "grid", gap: 10 }}>
            <SlotBlock
              label="Support"
              letter="S"
              help="Helps the responsible parties — provides resources, expertise, or effort without owning the outcome."
              dynamicMappingEnabled={!!dynamicMappingEnabled}
              mode={(rasci.support_mode as SlotMode | undefined) ?? "explicit"}
              roleTypeId={rasci.support_role_type_id ?? ""}
              candidates={candidates}
              selectedIds={supportIds}
              roleTypes={customerRoleTypes ?? []}
              onModeChange={(m) => patchRasci({ support_mode: m })}
              onRoleTypeChange={(id) => patchRasci({ support_role_type_id: id || null })}
              onMemberChange={(next) => {
                const { userIds, groupIds } = splitByKind(next);
                patchRasci({ support_user_ids: userIds, support_group_ids: groupIds });
              }}
            />
            <SlotBlock
              label="Consulted"
              letter="C"
              help="Two-way — input required before action is taken. Ask these people first."
              dynamicMappingEnabled={!!dynamicMappingEnabled}
              mode={(rasci.consulted_mode as SlotMode | undefined) ?? "explicit"}
              roleTypeId={rasci.consulted_role_type_id ?? ""}
              candidates={candidates}
              selectedIds={consultedIds}
              roleTypes={customerRoleTypes ?? []}
              onModeChange={(m) => patchRasci({ consulted_mode: m })}
              onRoleTypeChange={(id) => patchRasci({ consulted_role_type_id: id || null })}
              onMemberChange={(next) => {
                const { userIds, groupIds } = splitByKind(next);
                patchRasci({ consulted_user_ids: userIds, consulted_group_ids: groupIds });
              }}
            />
            <SlotBlock
              label="Informed"
              letter="I"
              help="One-way — kept in the loop after the fact. No action required from them."
              dynamicMappingEnabled={!!dynamicMappingEnabled}
              mode={(rasci.informed_mode as SlotMode | undefined) ?? "explicit"}
              roleTypeId={rasci.informed_role_type_id ?? ""}
              candidates={candidates}
              selectedIds={informedIds}
              roleTypes={customerRoleTypes ?? []}
              onModeChange={(m) => patchRasci({ informed_mode: m })}
              onRoleTypeChange={(id) => patchRasci({ informed_role_type_id: id || null })}
              onMemberChange={(next) => {
                const { userIds, groupIds } = splitByKind(next);
                patchRasci({ informed_user_ids: userIds, informed_group_ids: groupIds });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Email form ──────────────────────────────────────────────────────────────

export function EmailResponseForm({
  config,
  candidates,
  onChange
}: {
  config: EmailResponseConfig;
  candidates: MemberCandidate[];
  onChange: (next: EmailResponseConfig) => void;
}) {
  const selected = useMemo(
    () => [
      ...(config.recipient_user_ids ?? []),
      ...(config.recipient_contact_ids ?? []),
      ...(config.recipient_group_ids ?? [])
    ],
    [config.recipient_user_ids, config.recipient_contact_ids, config.recipient_group_ids]
  );

  function splitIntoKinds(ids: string[]): {
    userIds: string[];
    contactIds: string[];
    groupIds: string[];
  } {
    const byId = new Map(candidates.map((c) => [c.id, c]));
    const userIds: string[] = [];
    const contactIds: string[] = [];
    const groupIds: string[] = [];
    for (const id of ids) {
      const c = byId.get(id);
      if (!c) continue;
      if (c.kind === "user") userIds.push(id);
      else if (c.kind === "contact") contactIds.push(id);
      else if (c.kind === "group") groupIds.push(id);
    }
    return { userIds, contactIds, groupIds };
  }

  function patch(next: Partial<EmailResponseConfig>) {
    onChange({ ...config, ...next });
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div className="field">
        <span className="label">Recipients *</span>
        <MemberPicker
          mode="multi"
          allowedKinds={["user", "contact", "group"]}
          candidates={candidates}
          selected={selected}
          onChange={(next) => {
            const { userIds, contactIds, groupIds } = splitIntoKinds(next);
            patch({
              recipient_user_ids: userIds,
              recipient_contact_ids: contactIds,
              recipient_group_ids: groupIds
            });
          }}
          placeholder="Add recipients…"
        />
      </div>

      <label className="field">
        <span className="label">Subject template *</span>
        <input
          className="input"
          value={config.subject_template ?? ""}
          onChange={(e) => patch({ subject_template: e.target.value })}
          placeholder="e.g. {{operation_name}} — alert at {{evaluated_at}}"
        />
      </label>

      <label className="field">
        <span className="label">Body template *</span>
        <textarea
          className="input"
          rows={5}
          value={config.body_template ?? ""}
          onChange={(e) => patch({ body_template: e.target.value })}
        />
        <div className="help" style={{ marginTop: 4 }}>{INTERPOLATION_HELP}</div>
      </label>
    </div>
  );
}

// ── Webhook form ────────────────────────────────────────────────────────────

export function WebhookResponseForm({
  config,
  onChange
}: {
  config: WebhookResponseConfig;
  onChange: (next: WebhookResponseConfig) => void;
}) {
  const headersText = useMemo(() => {
    if (!config.headers || Object.keys(config.headers).length === 0) return "";
    return JSON.stringify(config.headers, null, 2);
  }, [config.headers]);

  const [headersDraft, setHeadersDraft] = useState(headersText);
  const [headersError, setHeadersError] = useState<string | null>(null);

  useEffect(() => {
    setHeadersDraft(headersText);
  }, [headersText]);

  function patch(next: Partial<WebhookResponseConfig>) {
    onChange({ ...config, ...next });
  }

  function commitHeaders() {
    const raw = headersDraft.trim();
    if (!raw) {
      patch({ headers: undefined });
      setHeadersError(null);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        patch({ headers: parsed as Record<string, string> });
        setHeadersError(null);
      } else {
        setHeadersError("Headers must be a JSON object of string → string.");
      }
    } catch (e) {
      setHeadersError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label className="field">
        <span className="label">URL *</span>
        <input
          className="input mono"
          value={config.url ?? ""}
          onChange={(e) => patch({ url: e.target.value })}
          placeholder="https://hooks.example.com/atlas"
        />
        <div className="help" style={{ marginTop: 4 }}>
          Must be a public https:// address. Internal network addresses are refused, and redirects are not followed.
        </div>
      </label>

      <label className="field">
        <span className="label">Method *</span>
        <select
          className="select"
          value={config.method ?? "POST"}
          onChange={(e) => patch({ method: e.target.value as "POST" | "PUT" })}
        >
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
        </select>
      </label>

      <label className="field">
        <span className="label">Headers (JSON object)</span>
        <textarea
          className="input mono"
          rows={4}
          value={headersDraft}
          onChange={(e) => setHeadersDraft(e.target.value)}
          onBlur={commitHeaders}
          placeholder={'{ "authorization": "Bearer …" }'}
        />
        {headersError && (
          <div className="help" style={{ color: "var(--danger-text)" }}>{headersError}</div>
        )}
      </label>

      <label className="field">
        <span className="label">Body template *</span>
        <textarea
          className="input mono"
          rows={6}
          value={config.body_template ?? ""}
          onChange={(e) => patch({ body_template: e.target.value })}
          placeholder={'{"op":"{{operation_name}}","rows":{{row_count}}}'}
        />
        <div className="help" style={{ marginTop: 4 }}>
          {INTERPOLATION_HELP} Substituted values in the body are JSON-escaped.
        </div>
      </label>

      <div className="help">
        10-second timeout. No retries in v1; failures are logged to the watch-events history.
      </div>
    </div>
  );
}

export function summarizeResponse(cfg: ResponseConfig): string {
  if (cfg.type === "action_item") {
    const sev = cfg.severity ? ` [${cfg.severity}]` : "";
    return `Action item${sev}${cfg.auto_resolve ? " · auto-resolve" : ""}`;
  }
  if (cfg.type === "email") {
    const count =
      (cfg.recipient_user_ids?.length ?? 0) +
      (cfg.recipient_contact_ids?.length ?? 0) +
      (cfg.recipient_group_ids?.length ?? 0);
    return `Email · ${count} recipient${count === 1 ? "" : "s"}`;
  }
  const url = cfg.url || "(no url)";
  return `Webhook · ${cfg.method ?? "POST"} ${url}`;
}


// ─── ADR-0075 — Slot source dropdown + slot block ─────────────────────────

function SlotSourceSelect({
  value,
  options,
  onChange
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (next: string) => void;
}) {
  return (
    <select
      className="select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "auto" }}
      aria-label="Slot source"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function RoleTypeSelect({
  value,
  roleTypes,
  onChange
}: {
  value: string;
  roleTypes: Array<{ id: string; label: string; slug: string; is_disabled: boolean }>;
  onChange: (id: string) => void;
}) {
  const active = roleTypes.filter((r) => !r.is_disabled);
  return (
    <>
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select role…</option>
        {active.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label} ({r.slug})
          </option>
        ))}
      </select>
      {active.length === 0 && (
        <div className="help muted" style={{ fontSize: 12, marginTop: 4 }}>
          No active role types defined. Add one in admin → Customers → Manage roles.
        </div>
      )}
    </>
  );
}

const SLOT_OPTIONS: Array<{ value: SlotMode; label: string }> = [
  { value: "explicit", label: "Explicit users / groups" },
  { value: "customer_primary", label: "Customer's primary user" },
  { value: "customer_role", label: "Customer's users with role…" },
  { value: "customer_team", label: "Customer's full team (all mapped users)" }
];

const MODE_HELP: Record<SlotMode, string> = {
  explicit: "",
  customer_primary:
    "At fire time, take the row's customer's primary mapped user. Walks parent customers on miss; falls back to default coverage.",
  customer_role:
    "At fire time, take the row's customer's users with this role. Walks parent customers on miss; slot left empty if nothing matches.",
  customer_team:
    "At fire time, take every user mapped to the row's customer (any role). Walks parent customers on miss; slot left empty if nothing matches."
};

function SlotBlock(props: {
  label: string;
  /** Single-letter RASCI badge: R / A / S / C / I. Drives color via the
   *  matching rasci{Letter} class on the fieldset. */
  letter: "R" | "A" | "S" | "C" | "I";
  help: string;
  dynamicMappingEnabled: boolean;
  mode: SlotMode;
  roleTypeId: string;
  candidates: MemberCandidate[];
  selectedIds: string[];
  roleTypes: Array<{ id: string; label: string; slug: string; is_disabled: boolean }>;
  onModeChange: (mode: SlotMode) => void;
  onRoleTypeChange: (id: string) => void;
  onMemberChange: (next: string[]) => void;
}): JSX.Element {
  const {
    label,
    letter,
    help,
    dynamicMappingEnabled,
    mode,
    roleTypeId,
    candidates,
    selectedIds,
    roleTypes,
    onModeChange,
    onRoleTypeChange,
    onMemberChange
  } = props;
  // When dynamic mapping is off, the slot mode is forced to "explicit" for
  // display only — persisted mode is preserved so re-enabling restores it.
  const effectiveMode: SlotMode = dynamicMappingEnabled ? mode : "explicit";
  return (
    <fieldset className={`rasciSlot rasci${letter}`}>
      <div className="rasciSlotHeader">
        <span className="rasciBadge" aria-hidden="true">{letter}</span>
        <legend className="label" style={{ margin: 0, padding: 0 }}>{label}</legend>
        {dynamicMappingEnabled && (
          <SlotSourceSelect
            value={effectiveMode}
            options={SLOT_OPTIONS}
            onChange={(m) => onModeChange(m as SlotMode)}
          />
        )}
      </div>
      {effectiveMode === "explicit" && (
        <MemberPicker
          mode="multi"
          allowedKinds={["user", "contact", "group"]}
          candidates={candidates}
          selected={selectedIds}
          onChange={onMemberChange}
          placeholder={`Add ${label.replace(" *", "").toLowerCase()}…`}
        />
      )}
      {effectiveMode === "customer_role" && (
        <RoleTypeSelect value={roleTypeId} roleTypes={roleTypes} onChange={onRoleTypeChange} />
      )}
      {effectiveMode !== "explicit" && (
        <div className="help muted" style={{ fontSize: 12 }}>
          {MODE_HELP[effectiveMode]}
        </div>
      )}
      {effectiveMode === "explicit" && help && (
        <div className="help muted" style={{ fontSize: 12 }}>{help}</div>
      )}
    </fieldset>
  );
}
